import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle,
  Image as ImageIcon,
  Code,
  Type,
  Music,
  Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { RichTextEditor } from '../../components/admin/RichTextEditor';
import { questionsApi, categoriesApi } from '../../services/api';

type ContentType = 'text' | 'code' | 'image' | 'audio' | 'math';
type Difficulty = 'easy' | 'medium' | 'hard';

interface QuestionContent {
  type: ContentType;
  content: string;
  language?: string;
  displayMode?: 'inline' | 'block';
}

interface QuestionOption {
  type: 'text' | 'code' | 'math' | 'image';
  content: string;
  isCorrect: boolean;
}

interface Category {
  id: string;
  name: string;
  examId: string;
}

const contentTypeIcons = {
  text: Type,
  code: Code,
  image: ImageIcon,
  audio: Music,
  math: Calculator,
};

export function QuestionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const [categoryId, setCategoryId] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [points, setPoints] = useState(1);
  const [timeLimit, setTimeLimit] = useState<number | null>(null);
  const [explanation, setExplanation] = useState('');
  const [contents, setContents] = useState<QuestionContent[]>([
    { type: 'text', content: '' },
  ]);
  const [options, setOptions] = useState<QuestionOption[]>([
    { type: 'text', content: '', isCorrect: true },
    { type: 'text', content: '', isCorrect: false },
    { type: 'text', content: '', isCorrect: false },
    { type: 'text', content: '', isCorrect: false },
  ]);

  useEffect(() => {
    loadCategories();
    if (id) loadQuestion(id);
  }, [id]);

  const loadCategories = async () => {
    try {
      const { data } = await categoriesApi.getAll();
      setCategories(data.items || data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadQuestion = async (questionId: string) => {
    setLoading(true);
    try {
      const { data } = await questionsApi.getById(questionId);
      setCategoryId(data.categoryId);
      setDifficulty(data.difficulty);
      setPoints(data.points);
      setTimeLimit(data.timeLimit);
      setExplanation(data.explanation || '');
      setContents(data.contents || [{ type: 'text', content: '' }]);
      setOptions(data.options || []);
    } catch (error) {
      console.error('Failed to load question:', error);
      toast.error('Erreur lors du chargement de la question');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!categoryId) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }
    if (contents.every((c) => !c.content.trim())) {
      toast.error('Veuillez ajouter du contenu à la question');
      return;
    }
    if (options.filter((o) => o.content.trim()).length < 2) {
      toast.error('Veuillez ajouter au moins 2 options');
      return;
    }
    if (!options.some((o) => o.isCorrect)) {
      toast.error('Veuillez marquer au moins une option comme correcte');
      return;
    }

    setSaving(true);
    try {
      const data = {
        categoryId,
        type: contents.length === 1 ? contents[0].type : 'mixed',
        difficulty,
        points,
        timeLimit,
        explanation,
        contents: contents.filter((c) => c.content.trim()),
        options: options.filter((o) => o.content.trim()),
      };

      if (isEditing) {
        await questionsApi.update(id!, data);
        toast.success('Question mise à jour');
      } else {
        await questionsApi.create(data);
        toast.success('Question créée');
      }
      navigate('/admin/questions');
    } catch (error) {
      console.error('Failed to save question:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addContent = (type: ContentType) => {
    setContents([...contents, { type, content: '' }]);
  };

  const updateContent = (index: number, updates: Partial<QuestionContent>) => {
    const newContents = [...contents];
    newContents[index] = { ...newContents[index], ...updates };
    setContents(newContents);
  };

  const removeContent = (index: number) => {
    if (contents.length > 1) {
      setContents(contents.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index: number, updates: Partial<QuestionOption>) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], ...updates };
    setOptions(newOptions);
  };

  const setCorrectOption = (index: number) => {
    setOptions(options.map((opt, i) => ({ ...opt, isCorrect: i === index })));
  };

  const addOption = () => {
    if (options.length < 6) {
      setOptions([...options, { type: 'text', content: '', isCorrect: false }]);
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      const wasCorrect = options[index].isCorrect;
      const newOptions = options.filter((_, i) => i !== index);
      if (wasCorrect && newOptions.length > 0) {
        newOptions[0].isCorrect = true;
      }
      setOptions(newOptions);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/questions')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Modifier la question' : 'Nouvelle question'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Utilisez l'éditeur pour créer des questions riches
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          <Save size={18} />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">Informations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Catégorie *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Difficulté
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as Difficulty)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="easy">Facile</option>
              <option value="medium">Moyen</option>
              <option value="hard">Difficile</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Points
            </label>
            <input
              type="number"
              value={points}
              onChange={(e) => setPoints(parseInt(e.target.value) || 1)}
              min={1}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Limite de temps (secondes)
            </label>
            <input
              type="number"
              value={timeLimit || ''}
              onChange={(e) => setTimeLimit(e.target.value ? parseInt(e.target.value) : null)}
              placeholder="Optionnel"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">Contenu de la question</h2>
          <div className="flex gap-2">
            {Object.entries(contentTypeIcons).map(([type, Icon]) => (
              <button
                key={type}
                onClick={() => addContent(type as ContentType)}
                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg"
                title={`Ajouter ${type}`}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>
        </div>

        {contents.map((content, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <GripVertical className="text-gray-400 cursor-move" size={18} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                {content.type}
              </span>
              {content.type === 'code' && (
                <input
                  type="text"
                  value={content.language || ''}
                  onChange={(e) => updateContent(index, { language: e.target.value })}
                  placeholder="Langage (ex: javascript)"
                  className="ml-auto px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
              )}
              {contents.length > 1 && (
                <button
                  onClick={() => removeContent(index)}
                  className="ml-auto p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            {content.type === 'text' ? (
              <RichTextEditor
                content={content.content}
                onChange={(html) => updateContent(index, { content: html })}
                placeholder="Écrivez votre question ici..."
              />
            ) : content.type === 'code' ? (
              <textarea
                value={content.content}
                onChange={(e) => updateContent(index, { content: e.target.value })}
                placeholder="Collez votre code ici..."
                className="w-full h-40 px-3 py-2 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
              />
            ) : content.type === 'math' ? (
              <div>
                <textarea
                  value={content.content}
                  onChange={(e) => updateContent(index, { content: e.target.value })}
                  placeholder="Entrez la formule LaTeX..."
                  className="w-full h-24 px-3 py-2 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={content.displayMode !== 'block'}
                      onChange={() => updateContent(index, { displayMode: 'inline' })}
                    />
                    Inline
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={content.displayMode === 'block'}
                      onChange={() => updateContent(index, { displayMode: 'block' })}
                    />
                    Block
                  </label>
                </div>
              </div>
            ) : (
              <input
                type="text"
                value={content.content}
                onChange={(e) => updateContent(index, { content: e.target.value })}
                placeholder={content.type === 'image' ? "URL de l'image" : "URL de l'audio"}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            )}
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">Options de réponse</h2>
          {options.length < 6 && (
            <button
              onClick={addOption}
              className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
            >
              <Plus size={16} />
              Ajouter une option
            </button>
          )}
        </div>

        {options.map((option, index) => (
          <div
            key={index}
            className={`flex items-start gap-3 p-4 border rounded-lg ${
              option.isCorrect
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <button
              onClick={() => setCorrectOption(index)}
              className={`mt-1 p-1 rounded-full ${
                option.isCorrect
                  ? 'text-emerald-600'
                  : 'text-gray-400 hover:text-emerald-500'
              }`}
              title={option.isCorrect ? 'Réponse correcte' : 'Marquer comme correcte'}
            >
              <CheckCircle size={20} />
            </button>
            <div className="flex-1">
              <input
                type="text"
                value={option.content}
                onChange={(e) => updateOption(index, { content: e.target.value })}
                placeholder={`Option ${index + 1}`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            {options.length > 2 && (
              <button
                onClick={() => removeOption(index)}
                className="mt-1 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-white">
          Explication (affichée après réponse)
        </h2>
        <RichTextEditor
          content={explanation}
          onChange={setExplanation}
          placeholder="Expliquez pourquoi la réponse correcte est bonne..."
        />
      </div>
    </div>
  );
}
